import { Fragment, useState } from 'react';

import { Popover } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { Tabs } from 'ui/components/nav';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { useGetNotifications } from 'ui/partials/hooks';
import { filterNotifsByDate } from 'ui/partials/transformers';
import c, { SIZE } from 'utils/constants';
import { cn, useAdmin } from 'utils/helpers';
import { MdClose } from 'react-icons/md';
import { useGetAdminNotifications } from 'modules/admin/notifications/hooks';
import { Notification } from 'utils/types';

import NotificationItem from './NotificationItem';
import NotificationTabActions from './NotificationTabActions';

const { UNREAD, READ } = c.NOTIFICATION_STATUSES;

const tabItems = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: READ,
    label: 'Read',
  },
  {
    value: UNREAD,
    label: 'Unread',
  },
];

const NotificationList = () => {
  const { isAdmin } = useAdmin();
  const {
    state: { isPreview },
  } = useAppWrapperContext();
  const [selectedTab, setSelectedTab] = useState('all');

  const { data: notifications } = (
    isAdmin && !isPreview ? useGetAdminNotifications : useGetNotifications
  )({
    status: selectedTab === 'all' ? '' : selectedTab.toUpperCase(),
  });
  const [selectedNotifications, setSelectedNotifications] = useState<
    Notification[] | undefined
  >([]);

  const handleSelect = (val: any) => {
    setSelectedNotifications((prevVal: any) => {
      const rec = prevVal.find((v: any) => v._id === val._id);
      if (rec) {
        return prevVal.filter((v: any) => v._id !== rec._id);
      }
      return [...prevVal, val];
    });
  };

  const clearSelected = () => setSelectedNotifications([]);
  const handleTabChange = ({ value }: { value: string }) => {
    setSelectedTab(value);
    clearSelected();
  };

  const handleBulkSelect = () => {
    if (selectedNotifications?.length !== notifications?.length) {
      return setSelectedNotifications(notifications);
    }
    return clearSelected();
  };

  const datesWithNotifs = filterNotifsByDate(notifications);

  const renderNotifs = Object.entries(datesWithNotifs).map(([key, values]) => (
    <Fragment key={key}>
      <div
        className={cn(
          'font-bold leading-[22px] text-[#022641] text-sm',
          'mx-[14px] py-[10px]',
          'border-b-[0.5px] border-b-[#E4EDF0]',
        )}
      >
        {key}
      </div>
      {values
        .sort((a: any, b: any) => a.createdAt < b.createdAt)
        .map((value: any) => (
          <NotificationItem
            isChecked={
              !!selectedNotifications?.find(
                (notif: any) => value._id === notif._id,
              )
            }
            onSelect={handleSelect}
            key={value._id}
            data={value}
          />
        ))}
    </Fragment>
  ));

  return (
    <Popover.Panel
      className={cn(
        'p-0 bg-fill-tertiary w-[420px]',
        'text-sm',
        'shadow-leapify-md',
        isPreview && 'mt-7',
      )}
    >
      <div
        className={cn(
          'bg-fill-primary pl-4 pr-[18px] py-[9px]',
          'flex justify-between items-center',
        )}
      >
        <div className="font-bold sticky top-0 text-sm leading-[22px] tracking-[0.04em]">
          Notification
        </div>
        <Popover.Button>
          <MdClose className="w-4 h-4 hover:text-danger-base transition" />
        </Popover.Button>
      </div>
      <div
        className={cn(
          'flex items-center justify-between',
          'border-b-[0.5px] border-separator-primary',
        )}
      >
        <Tabs
          type="secondary"
          size={SIZE.Small}
          selectedTab={selectedTab}
          items={tabItems}
          onChange={handleTabChange}
        />
        <Link
          to="/notifications"
          className="text-sm text-info-base font-semibold pr-[18px]"
        >
          View all
        </Link>
      </div>
      <div
        className={cn(
          selectedNotifications?.length ? 'h-[106px]' : 'h-0',
          'duration-300 ease-in-out transform transition-all',
        )}
      >
        <NotificationTabActions
          tabSelected={selectedTab}
          itemsSelected={selectedNotifications ?? []}
          handleCancel={clearSelected}
          handleBulkSelect={handleBulkSelect}
          notificationsCount={notifications?.length ?? 0}
        />
      </div>
      <div
        style={{ overflowY: 'overlay' as any }}
        className={cn('h-[calc(100vh_-_90px)]', 'bg-fill-tertiary')}
      >
        {renderNotifs}
      </div>
    </Popover.Panel>
  );
};

export default NotificationList;
